import { Injectable } from '@angular/core';
import { matrix, type EChartsOption } from 'echarts';

export interface HeatmapExtraFields {
  [key: string]: Array<Array<number | string>>;
}

// CORREÇÃO: Adicionada a propriedade 'isPercentage' à interface HeatmapParams
export interface HeatmapParams {
  matrix: number[][];
  rows: (string | number)[];
  columns: (string | number)[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  tooltipValueLabel?: string;
  title?: string;
  unit?: string;
  min?: number;
  max?: number;
  palette?: string[];
  extraFields?: HeatmapExtraFields;
  transpose?: boolean;
  smoothing?: boolean;
  isPercentage?: boolean; // <-- NOVO!
}

@Injectable({ providedIn: 'root' })
export class HeatmapService {
  public readonly divergingRedWhiteBlue = [
    '#a50f15', 
    '#ffffff', 
    '#08519c', 
  ];

  public readonly divergingRedBlue = [
    '#a50f15', 
    '#08519c' 
  ];

  // Alternativa com mais gradações
  public readonly divergingRedBlueGradient = [
    '#a50f15', // vermelho escuro
    '#de2d26', // vermelho médio
    '#3182bd', // azul médio
    '#08519c'  // azul escuro
  ];

  public readonly divergingRedBlueSoft = [
    '#d73027', 
    '#4575b4'
  ];

  public readonly lossPalette = [
    //'#e5f5e0', 
    //'#693c15' 
    //'#9dfefe', // Azul Turquesa (Superlinear)
    '#2a6283',
    //'#9dfefe',
    //'#79a4c3',
    //'#2a6283',
    //'#1d4a64',
    //'#ff8205',
    //'#e2e233', 
    //'#ff4500', // Laranja avermelhado
    '#e47d41',
    '#000000'
  ];

  private generateRgbPalette(
    steps: number,
    startHex: string,
    endHex: string
  ): string[] {
    const hex2rgb = (h: string) =>
      h.slice(1).match(/.{2}/g)!.map(x => parseInt(x, 16));
    const [r1, g1, b1] = hex2rgb(startHex);
    const [r2, g2, b2] = hex2rgb(endHex);
    return Array.from({ length: steps }, (_, i) => {
      const t = i / (steps - 1);
      const r = Math.round(r1 + (r2 - r1) * t);
      const g = Math.round(g1 + (g2 - g1) * t);
      const b = Math.round(b1 + (b2 - b1) * t);
      return `#${[r, g, b].map(c => c.toString(16).padStart(2, '0')).join('')}`;
    });
  }

  /** “strict red→blue” cobrindo –100…+100 em Δ=0.1 */
  public readonly strictRedBluePalette = (() => {
    const totalSteps = 2001;    // (100 - (-100)) / 0.1 + 1
    const negSteps = Math.floor((-0.1 - (-100)) / 0.1) + 1; // 1000
    const posSteps = totalSteps - negSteps;               // 1001

    // 1) vermelho escuro → vermelho claro (−100 → −0.1)
    const neg = this.generateRgbPalette(negSteps, '#a50f15', '#fcaeae');

    // 2) azul claro → azul escuro (0.1 → +100)
    const pos = this.generateRgbPalette(posSteps, '#9ecae1', '#08519c');

    return [...neg, ...pos];
  })();

  public readonly warmCoolPalette = this.divergingRedWhiteBlue;

  private readonly brBGPalette = [
    '#0270BF', '#2683A3', '#4A9788', '#6EAB6D', '#92BE52',
    '#B6D037', '#DAE61C', '#FFFA01', '#FFFA33'
  ];

    public readonly diverging5 = [
    '#a50f15', // –100%
    '#fcaeae', // –5%
    '#ffffff', //   0%
    '#9ecae1', //  +5%
    '#08519c'  // +100%
  ];

  public readonly customSharpDivergingColors = {
    darkRed: '#a50f15',
    lightRed: '#fcaeae',
    white: '#ffffff',
    lightBlue: '#9ecae1',
    darkBlue: '#08519c'
  };

  public readonly brBGPalette2 = [
  '#543005', '#794608', '#ad7021',
  '#d8b46c', '#f6e9c5', '#e2f0ee',
  '#87d0c5', '#1f827a', '#003c30'
];


public readonly coolwarmPalette = [
  '#3b4cc0', '#6282ea', '#8db0fe',
  '#b9d0f9', '#dddcdc', '#f5c4ac',
  '#f4987a', '#dd5f4b', '#b40426'
];

  public readonly infernoPalette = [
    '#28004d', '#631017', '#9a3038', '#d43f31',
    '#e87729', '#f1c53b', '#fcffa4'
  ];

  private readonly rdYlGnPalette = [
    '#006837', '#1a9850', '#66bd63', '#a6d96a', '#d9ef8b',
    '#fee08b', '#fdae61', '#f46d43', '#d73027', '#a50026'
  ];
  public readonly gistHeatPalette = [
    // Original 0-20% colors removed as per request to start darker
    '#FDD0A2', // Original 30% color, now the start of the palette
    '#FDAE6B', // Original 40% color
    '#F46D43', // Original 50% color (reddish-orange)
    '#CB181D', // Original 60% color (red)
    '#A50F15', // Original 70% color (dark red)
    '#67000D', // Original 80% color (very dark red)
    '#330000', // Original 90% color (extremely dark red, almost black)
    '#000000'  // Original 100% color (black)
  ];

  public readonly redGradientPalette = [
    '#FF8A8A',
    '#FF4D4D',
    '#E60000',
    '#B30000',
    '#660000'
  ];

  public readonly yellowOrangePurpleBlackPalette = [
    '#ffffdb', '#ffe79c', '#fec44f', '#fe9929', '#ec7014',
    '#b25d9d', '#7a3eb2', '#542788', '#2d0059', '#000000'
  ];

  // Paleta com transição direta azul claro -> vermelho claro (sem roxo)
  public readonly divergingBlueRed = [
    '#6baed6', // azul claro para valores negativos
    '#fb6a4a'  // vermelho claro para valores positivos
  ];

  // Alternativa com mais tons (USADA PARA SPEEDUP)
  public readonly divergingBlueRedGradient = [
    '#3182bd', // azul médio
    '#6baed6', // azul claro
    '#f9b9b4', // azul claro
    '#fb6a4a', // vermelho claro
    '#de2d26'  // vermelho médio
  ];

  // Outra opção com tons bem suaves
  public readonly divergingSoftBlueRed = [
    '#74a9cf', // azul suave
    '#f768a1'  // rosa/vermelho suave
  ];

  buildGenericOption(p: HeatmapParams): EChartsOption {
    return this.buildHeatmapOption({
      ...p,
      palette: p.palette ?? this.brBGPalette
    });
  }

  buildEfficiencyOption(args: {
    matrix: number[][];
    rows: (string | number)[];
    columns: (string | number)[];
    times: number[][];
    seqTimes: number[][];
    transpose?: boolean;
    smoothing?: boolean;
    idealMode?: boolean;
  }): EChartsOption {
    const { idealMode = false, ...rest } = args;

    const scaledMatrix = args.matrix.map(row =>
      row.map(val => val * 100)
    );

    const extra: HeatmapExtraFields = {
      'Par. runtime (s)': args.times,
      'Seq. runtime (s)': args.seqTimes
    };

    return this.buildHeatmapOption({
      ...rest,
      matrix: scaledMatrix,
      title: idealMode ? 'Ideal efficiency' : 'Efficiency',
      unit: '%',
      palette: this.yellowOrangePurpleBlackPalette,
      min: 0,
      max: 100,
      extraFields: extra,
      isPercentage: true // Adicionar para o build de eficiência
    });
  }

  buildImbalanceOption(args: {
    matrix: number[][];
    imbalances: number[][];
    rows: (string | number)[];
    columns: (string | number)[];
    transpose?: boolean;
    smoothing?: boolean;
  }): EChartsOption {
    const extra: HeatmapExtraFields = {
      'Imbalance (%)': args.imbalances
    };

    return this.buildHeatmapOption({
      matrix: args.imbalances,
      rows: args.rows,
      columns: args.columns,
      title: 'Imbalance',
      unit: '%',
      palette: this.gistHeatPalette,
      min: 0,
      max: 100,
      extraFields: extra,
      transpose: args.transpose,
      smoothing: args.smoothing,
      isPercentage: true // Adicionar para o build de Imbalance
    });
  }

  // CORREÇÃO: Adicionada a propriedade 'isPercentage' ao argumento de buildSmoothedHeatmapOption
  buildSmoothedHeatmapOption(args: {
    matrix: number[][];
    originalRows: (string | number)[];
    originalColumns: (string | number)[];
    palette?: string[];
    min?: number;
    max?: number;
    title?: string;
    unit?: string;
    size?: number;
    isPercentage?: boolean; // <-- NOVO!
  }): EChartsOption {
    const size = args.size ?? 100;
    
    // CORREÇÃO: Aplica a multiplicação por 100 APENAS se isPercentage for true
    const mat = this.interpolateMatrix(args.matrix, size).map(row =>
      row.map(val => args.isPercentage ? val * 100 : val)
    );
    
    const axisCategories = Array.from({ length: size }, (_, i) => i); // Categories are 0 to size-1

    const seriesData: (number | string)[][] = [];
    for (let i = 0; i < size; i++) { // i is data row index (0 for top row of mat)
      for (let j = 0; j < size; j++) { // j is data col index
        const value = Number(mat[i][j]) || 0;
        seriesData.push([j, i, +value.toFixed(2)]); // Store raw j and i (data indices)
      }
    }
    const finiteValues = seriesData.map(d => d[2] as number).filter(Number.isFinite);
    const valueMin = args.min ?? Math.min(...finiteValues);
    const valueMax = args.max ?? Math.max(...finiteValues);
    
    const yAxisInverse = true; // Category 0 at TOP, category size-1 at BOTTOM

    const createAxisLabelFormatter = (
      originalLabels: (string | number)[],
      axisSize: number
    ) => {
      return (value: number, index: number) => { // value is category index 0..axisSize-1
        const originalLength = originalLabels?.length ?? 0;
        if (originalLength === 0) return '';

        for (let k = 0; k < originalLength; k++) { // k is index in originalLabels
          let idealTickPosition: number;
          if (originalLength <= 1) { // Handles 0 and 1 label cases
            idealTickPosition = 0; // Place single label at the first category (top/left)
          } else {
            idealTickPosition = Math.round((k / (originalLength - 1)) * (axisSize - 1));
          }

          if (value === idealTickPosition) {
            return originalLabels[k].toString();
          }
        }
        return ''; // Hide other labels
      };
    };

    return {
      animation: false,
      title: { text: args.title ?? '', left: 'center' },
      tooltip: { show: false },
      xAxis: {
        type: 'category',
        data: axisCategories,
        position: 'top',
        splitArea: { show: true },
        axisLabel: {
          interval: 0,
          hideOverlap: true,
          formatter: (value: number, index: number) => {
            const cols = args.originalColumns;
            const n = cols?.length ?? 0;
            if (n === 0) return '';
            if (n === 1) return cols[0].toString();

            for (let k = 0; k < n; k++) {
              const pos = Math.round((k / (n - 1)) * (size - 1));
              //console.log("index: "+index+" | pos: "+pos);
              if (index === pos) {
                return cols[k].toString();
              }
            }
            return '';
          }
        },
        axisTick: {
          alignWithLabel: true,
          interval: (_, lbl) => lbl !== ''
        }
      },
      yAxis: {
        type: 'category',
        data: axisCategories,
        inverse: true,
        splitArea: { show: true },
        axisLabel: {
          interval: 0,
          hideOverlap: true,
          formatter: (value: number, index: number) => {
            const rows = args.originalRows;
            //console.log(rows);
            const n = rows?.length ?? 0;
            if (n === 0) return '';
            if (n === 1) return rows[0].toString();
            
            for (let k = 0; k < n; k++) {
              const pos = Math.round((k / (n - 1)) * (size - 1));
              if (index === pos) {
                return rows[k].toString();
              }
            }
            return '';
          }
        },
        axisTick: {
          alignWithLabel: true,
          interval: (_, lbl) => lbl !== ''
        }
      },
      grid: { height: '70%', top: 60 },
      visualMap: {
        min: valueMin,
        max: valueMax,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        inRange: { color: args.palette ?? this.yellowOrangePurpleBlackPalette }
      },
      series: [{
        type: 'heatmap',
        silent: true,
        animation: false,
        progressive: 5000,
        progressiveThreshold: 20000,
        data: seriesData.map(([x_data_idx, y_data_idx, val]) => [
          x_data_idx, 
          Number(y_data_idx), 
          val
        ]),
        emphasis: { disabled: true }
      }]
    } as EChartsOption;
  }

  buildHeatmapOption(p: HeatmapParams): EChartsOption {
    const {
      matrix,
      rows,
      columns,
      title = '',
      unit = '%',
      min,
      max,
      palette = this.brBGPalette,
      extraFields = {},
      transpose = false,
      smoothing = false,
      xAxisLabel = 'X',
      yAxisLabel = 'Y',
      tooltipValueLabel = 'Value',
      // isPercentage já está em p, mas não precisa de desestruturação aqui
    } = p;

    const source = smoothing ? this.smooth(matrix) : matrix;
    const [r, c] = transpose ? [columns, rows] : [rows, columns];
    const mat = transpose ? this.transpose(source) : source;

    const finalExtraFields: HeatmapExtraFields = {};
    Object.entries(extraFields).forEach(([key, value]) => {
      finalExtraFields[key] = transpose ? this.transpose(value) : value;
    });

    const seriesData: (number | string)[][] = [];
    for (let i = 0; i < mat.length; i++) {
      for (let j = 0; j < mat[i].length; j++) {
        const value = Number(mat[i][j]) || 0;
        seriesData.push([j, i, +value.toFixed(2)]);
      }
    }

    const finiteValues = seriesData.map(d => d[2] as number).filter(Number.isFinite);
    const valueMin = min ?? Math.min(...finiteValues);
    const valueMax = max ?? Math.max(...finiteValues);
    const yChartReverse = true;

    return {
      animation: false,
      title: { text: title, left: 'center' },
      tooltip: {
        position: 'top',
        formatter: (params: any) => {
          const [xChart, yChartRaw, v] = params.value;
          const yDataIndex = yChartReverse ? (r.length - 1 - yChartRaw) : yChartRaw;

          const rowLabel = r[yDataIndex];
          const colLabel = c[xChart];

          const formatExtraFieldValue = (value: number | string): string => {
            if (typeof value === 'string') return value;
            const num = Number(value);
            if (Number.isNaN(num)) return 'N/A';
            if (num === 0) return '0.000';
            const absNum = Math.abs(num);
            if (absNum > 0 && absNum < 0.001) {
              return num.toExponential(2);
            }
            return num.toFixed(3);
          };

          const extras = Object.entries(finalExtraFields)
            .map(([k, mtx]) => {
              const val = mtx?.[yDataIndex]?.[xChart] ?? 'N/A';
              return `${k}: ${formatExtraFieldValue(val)}`;
            })
            .join('<br/>');

          return `${yAxisLabel}: ${rowLabel}<br/>${xAxisLabel}: ${colLabel}<br/>${tooltipValueLabel}: ${formatExtraFieldValue(v)}${unit}${extras ? `<br/>${extras}` : ''}`;
        }
      },
      grid: { height: '70%', top: 60 },
      xAxis: {
        type: 'category',
        data: c,
        position: 'top',
        splitArea: { show: true }
      },
      yAxis: {
        type: 'category',
        data: yChartReverse ? [...r].reverse() : r,
        splitArea: { show: true }
      },
      visualMap: {
        min: valueMin,
        max: valueMax,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: 0,
        // se for diverging, passa só 3 cores para gerar um gradiente contínuo
        inRange: {
          color: p.palette?.length === 3
            ? p.palette 
            : p.palette!
        }
      },
      series: [{
        type: 'heatmap',
        data: seriesData.map(([x, y, val]) => [
          x,
          yChartReverse ? (r.length - 1 - Number(y)) : y,
          val
        ]),
        emphasis: {
          itemStyle: {
            shadowBlur: 5,
            shadowColor: 'rgba(0,0,0,0.3)'
          }
        }
      }]
    } as EChartsOption;
  }

  transpose<T>(mat: T[][]): T[][] {
    if (!mat || mat.length === 0 || !mat[0] || mat[0].length === 0) {
      return [];
    }
    return mat[0].map((_, i) => mat.map(row => row[i]));
  }

  private smooth(mat: number[][], step = 0.25): number[][] {
    if (step >= 1) return mat;

    const rows = mat.length;
    const cols = mat[0]?.length || 0;
    const newRows = Math.floor((rows - 1) / step) + 1;
    const newCols = Math.floor((cols - 1) / step) + 1;
    const out: number[][] = [];

    for (let i = 0; i < newRows; i++) {
      const y = i * step;
      const y0 = Math.floor(y);
      const y1 = Math.min(y0 + 1, rows - 1);
      const ty = y - y0;

      const row: number[] = [];
      for (let j = 0; j < newCols; j++) {
        const x = j * step;
        const x0 = Math.floor(x);
        const x1 = Math.min(x0 + 1, cols - 1);
        const tx = x - x0;

        const v00 = mat[y0]?.[x0] || 0;
        const v01 = mat[y0]?.[x1] || 0;
        const v10 = mat[y1]?.[x0] || 0;
        const v11 = mat[y1]?.[x1] || 0;

        const top = v00 * (1 - tx) + v01 * tx;
        const bottom = v10 * (1 - tx) + v11 * tx;
        row.push(top * (1 - ty) + bottom * ty);
      }
      out.push(row);
    }
    //console.table(out);
    return out;
  }

  /** Interpola a matriz para uma grade NxN usando bilinear */
  interpolateMatrix(mat: number[][], size = 100): number[][] {
    const rows = mat.length;
    const cols = mat[0]?.length || 0;
    const out: number[][] = [];
    for (let i = 0; i < size; i++) {
      const y = (i * (rows - 1)) / (size - 1);
      const y0 = Math.floor(y);
      const y1 = Math.min(y0 + 1, rows - 1);
      const ty = y - y0;
      const row: number[] = [];
      for (let j = 0; j < size; j++) {
        const x = (j * (cols - 1)) / (size - 1);
        const x0 = Math.floor(x);
        const x1 = Math.min(x0 + 1, cols - 1);
        const tx = x - x0;
        const v00 = mat[y0]?.[x0] ?? 0;
        const v01 = mat[y0]?.[x1] ?? 0;
        const v10 = mat[y1]?.[x0] ?? 0;
        const v11 = mat[y1]?.[x1] ?? 0;
        const top = v00 * (1 - tx) + v01 * tx;
        const bottom = v10 * (1 - tx) + v11 * tx;
        row.push(top * (1 - ty) + bottom * ty);
      }
      out.push(row);
    }
    //console.table(out);

    return out;
  }
}
